import api from "api";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import "./ticket.scss";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { TwitterPicker } from "react-color";
import { RootState, TicketType } from "types";
import { useSelector, useDispatch } from "react-redux";
import { actions } from "redux/reducers";
import Loader from "components/loader";
import Checklist from "./checklist/checklist";
import Attachments from "./attachments/index";
import Activity from "./activity";
import { toast } from "react-toastify";
import TextareaAutosize from "react-textarea-autosize";

function Ticket({ deleteTicket, refresh }: { deleteTicket: (id: number) => void; refresh: () => void }) {
    const params: any = useParams();
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const state = useSelector((state: RootState) => state);
    const user = useSelector((state: RootState) => state.user);

    //const [isAdmin, setIsAdmin] = useState(false);
    const [boardMembers, setBoardMembers] = useState<
        {
            Id: number;
            BoardId: number;
            ContactId: number;
            RoleId: number;
            Name: string;
            Surname: string;
            Email: string;
            IsAdmin: boolean;
        }[]
    >([]);
    const [userId, setUserId] = useState(0);
    const [open, setOpen] = useState(false);
    const [ticket, setTicket] = useState<TicketType | null>(null);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [showDuplicateSettings, setShowDuplicateSettings] = useState<boolean>(false);
    const [showCopySettings, setShowCopySettings] = useState<boolean>(false);
    const [duplicationNumber, setDuplicationNumber] = useState<number>(1);
    const [selectedBoard, setSelectedBoard] = useState<number>(0);
    const [newLabel, setNewLabel] = useState({
        open: false,
        description: "",
        colour: "",
        colourOpen: false,
    });
    const labelRef = useRef<HTMLInputElement>(null);

    //const ref = useRef();

    const updateTicket = () => {
        api.ticket.update(ticket!.TicketDetails.Id, user.Id, ticket!.TicketDetails.Heading, ticket!.TicketDetails.Description ? ticket!.TicketDetails.Description.replaceAll("\n", "\\n") : "", ticket!.TicketDetails.DueDate!).then(() => {
            dispatch(actions.board.set({ ...state.board }));
            toast.success("ticket saved");
        });
    };

    const addTicketMember = (e: any) => {
        const mem = state.clientContacts.find((x: any) => x.Id === parseInt(e.currentTarget.value));
        api.ticket.member.add(ticket!.TicketDetails.Id, e.currentTarget.value).then((id) => {
            dispatch(actions.board.set({ ...state.board }));
            setTicket({
                ...ticket!,
                Members: [
                    ...ticket!.Members,
                    {
                        Id: id,
                        Name: mem.Name,
                        Surname: mem.Surname,
                        Email: mem.Email,
                    },
                ],
            });
        });
    };

    const addToBoard = (e: any) => {
        setSelectedBoard(e.currentTarget.value);
    };

    const addTicketLabel = () => {
        if (newLabel.description === "") {
            toast.warning("Please add a title");
            labelRef?.current?.focus();
            return;
        }

        api.ticket.label.add(params.ticketId, user.Id, newLabel.description, newLabel.colour).then((id) => {
            dispatch(actions.board.set({ ...state.board }));
            setTicket({
                ...ticket!,
                Labels: [
                    ...(ticket!.Labels || []),
                    {
                        Id: id,
                        TicketId: params.ticketId,
                        Description: newLabel.description,
                        Colour: newLabel.colour,
                        DateCreated: new Date(),
                    },
                ],
            });
            setNewLabel({
                open: false,
                colour: "",
                colourOpen: false,
                description: "",
            });
        });
    };

    const removeTicketLabel = (id: number) => {
        api.ticket.label.delete(id).then(() => {
            dispatch(actions.board.set({ ...state.board }));
            setTicket({
                ...ticket!,
                Labels: ticket!.Labels?.filter((x) => x.Id !== id),
            });
        });
    };

    const removeTicketMember = (id: number) => {
        setTicket({
            ...ticket!,
            Members: ticket!.Members.filter((x) => x.Id !== id),
        });
        api.ticket.member.remove(ticket!.TicketDetails.Id, id).then(() => {
            dispatch(actions.board.set({ ...state.board }));
        });
    };

    const assignTicket = (e: any) => {
        api.ticket.assign(ticket!.TicketDetails.Id, e.currentTarget.value).then(() => {
            refresh();
        });
        setTicket({
            ...ticket!,
            TicketDetails: {
                ...ticket!.TicketDetails,
                AssigneeId: parseInt(e.currentTarget.value),
            },
            Assignee: state.clientContacts.find((x: any) => x.Id === parseInt(e.currentTarget.value)),
        });
    };

    const removeAssignee = () => {
        api.ticket.unAssign(ticket!.TicketDetails.Id).then(() => {
            refresh();
        });
        setTicket({
            ...ticket!,
            Assignee: null,
        });
    };

    const closeTicket = () => {
        const pathName = location.pathname;
        const newPath = pathName.slice(0, pathName.indexOf("/ticket"));
        history.replace(newPath, null);
        refresh();
    };

    const handleDuplication = () => {
        api.ticket.duplicate({ Id: params.ticketId, DuplicationAmount: duplicationNumber }).then(() => {
            toast.success("Card duplicate");
            refresh();
        });
    };

    const handleDuplicationNumberChange = (state: string) => {
        if (state === "increase") {
            if (duplicationNumber >= 100) {
                setDuplicationNumber(100);
            } else {
                setDuplicationNumber(duplicationNumber + 1);
            }
        } else {
            if (duplicationNumber <= 1) {
                setDuplicationNumber(1);
            } else {
                setDuplicationNumber(duplicationNumber - 1);
            }
        }
    };

    const handleCopyToBoard = () => {
        api.ticket.copy({ Id: params.ticketId, BoardId: selectedBoard }).then(() => {
            toast.success("Copied to board");
            refresh();
        });
    };

    useEffect(() => {
        if (params.ticketId) {
            setLoading(true);
            api.ticket.get(params.ticketId).then((data) => {
                setLoading(false);
                setTicket(data);
                setOpen(true);
            });
        } else {
            setOpen(false);
        }
    }, [params.ticketId]);

    useEffect(() => {
        if (state.board?.Members && state.user?.Id) {
            setUserId(state.user.Id);
            setBoardMembers(state.board.Members);
        }
    }, [state.board?.Members, state.user?.Id]);

    // useEffect(() => {
    //     if (boardMembers.length > 0 && userId > 0) {
    //         boardMembers.map((x: any, i: number) => {
    //             //console.log("HERRREEE", x.RoleId, x.Id, userId);
    //             if (x.RoleId === 2 && x.Id == userId) {
    //                 console.log("HERRREEE", x, x.RoleId, x.Id, user, userId);
    //                 setIsAdmin(true);
    //             }
    //         });
    //     }
    //     //console.log(isAdmin);
    // }, [userId, boardMembers]);

    const handleKeyDown = (e: any) => {
        e.target.style.height = "inherit";
        e.target.style.height = `${e.target.scrollHeight + 10}px`;
    };

    // const getHeight = () => {
    //     var height = 0;
    //     if (ticket !== undefined) {
    //         if (ticket?.Description && ticket?.Description.length > 0) {
    //             return ticket?.Description.length + "px";
    //         }
    //         else {
    //             return "150px";
    //         }
    //     } else {
    //         return "150px";
    //     }
    // };

    return (
        <>
            <div id="ticket" className={`${open ? "open" : ""} ${expanded ? `expanded` : ``}`}>
                <div className="ticket-header">
                    <i className="fal fa-times" onClick={closeTicket}></i>
                    <input
                        type="text"
                        value={ticket?.TicketDetails.Heading || ""}
                        //disabled={isAdmin}
                        onChange={(e) => {
                            setTicket({
                                ...ticket!,
                                TicketDetails: {
                                    ...ticket!.TicketDetails,
                                    Heading: e.currentTarget.value,
                                },
                            });
                        }}
                        onBlur={updateTicket}
                        onKeyUp={(e) => {
                            if (e.key === "Enter") {
                                e.currentTarget.blur();
                            }
                        }}
                    />
                    <i className={`far ${expanded ? `fa-compress-alt` : `fa-expand`}`} onClick={() => setExpanded(!expanded)}></i>
                </div>
                <div className="ticket-body">
                    <div className="left">
                        <div className="action-buttons">
                            {/* <span className={`complete ${ticket?.IsCompleted ? "completed" : ""}`} onClick={() => {}}>
                                <i className="far fa-check"></i> {ticket?.IsCompleted ? "Completed" : "Complete"}
                            </span> */}
                            <span
                                className="duplicate noselect"
                                onClick={() => {
                                    setShowCopySettings(!showCopySettings);
                                    setShowDuplicateSettings(false);
                                }}
                            >
                                <i className={showCopySettings ? "far fa-chevron-up" : "far fa-chevron-down"} /> Copy
                            </span>
                            <span
                                className="duplicate noselect"
                                onClick={() => {
                                    setShowDuplicateSettings(!showDuplicateSettings);
                                    setShowCopySettings(false);
                                }}
                            >
                                <i className={showDuplicateSettings ? "far fa-chevron-up" : "far fa-chevron-down"}></i> Duplicate
                            </span>
                            <span className="delete noselect" onClick={() => deleteTicket(params.ticketId)}>
                                <i className="far fa-trash"></i> Delete
                            </span>
                        </div>
                        {showDuplicateSettings && (
                            <div className="duplication-settings">
                                <div className="options noselect">
                                    <span className="text">Duplication amount:</span>
                                    <input
                                        type="number"
                                        value={duplicationNumber}
                                        min={1}
                                        max={100}
                                        onChange={(e) => {
                                            setDuplicationNumber(+e.currentTarget.value);
                                        }}
                                    />
                                    <span
                                        className="increment no-select"
                                        onClick={() => {
                                            handleDuplicationNumberChange("increase");
                                        }}
                                    >
                                        <i className="far fa-plus" />
                                    </span>
                                    <span
                                        className="increment no-select"
                                        onClick={() => {
                                            handleDuplicationNumberChange("decrease");
                                        }}
                                    >
                                        <i className="far fa-minus" />
                                    </span>
                                </div>
                                <div className="submit">
                                    <span
                                        className="submit-button"
                                        onClick={() => {
                                            handleDuplication();
                                        }}
                                    >
                                        <i className="far fa-clone"></i> Duplicate selected ticket
                                    </span>
                                </div>
                            </div>
                        )}
                        {showCopySettings && (
                            <div className="duplication-settings noselect">
                                <select value={selectedBoard} className="board-selection" title="Members" name="ticket-members-select" onChange={addToBoard}>
                                    <option value={0} disabled>
                                        Add
                                    </option>
                                    {state.boards.map((x: any, i: number) => {
                                        return (
                                            <option value={x.Board.Id} key={i}>
                                                {x.Board.Description}
                                            </option>
                                        );
                                    })}
                                </select>
                                <div className="submit">
                                    <span
                                        className="submit-button"
                                        onClick={() => {
                                            handleCopyToBoard();
                                        }}
                                    >
                                        <i className="far fa-clone"></i> Copy selected ticket
                                    </span>
                                </div>
                            </div>
                        )}
                        <div className="input-group">
                            <label>Description</label>
                            <TextareaAutosize
                                onKeyUp={(e) => {
                                    handleKeyDown(e);
                                }}
                                maxRows={200}
                                minRows={4}
                                id="ticket-description"
                                title="Ticket Description"
                                onBlur={(e) => {
                                    updateTicket();
                                }}
                                //disabled={isAdmin}
                                value={ticket?.TicketDetails.Description ? ticket?.TicketDetails.Description.replaceAll("\\n", "\n") : " "}
                                onChange={(e) => {
                                    setTicket({
                                        ...ticket!,
                                        TicketDetails: {
                                            ...ticket!.TicketDetails,
                                            Description: e.currentTarget.value,
                                        },
                                    });
                                }}
                            ></TextareaAutosize>
                        </div>
                        <Checklist item={ticket?.Checklist} ticketId={ticket?.TicketDetails?.Id} />
                        <Attachments />
                        <div className="input-group">
                            <label>Members</label>
                            {!ticket?.Members?.length ? (
                                <select value={0} title="Members" name="ticket-members-select" onChange={addTicketMember}>
                                    <option value={0} disabled>
                                        Add
                                    </option>
                                    {state.clientContacts.map((x: any, i: number) => {
                                        return (
                                            <option value={x.Id} key={i}>
                                                {x.Name} {x.Surname}
                                            </option>
                                        );
                                    })}
                                </select>
                            ) : (
                                <div>
                                    {ticket?.Members?.map((e: any) => {
                                        return (
                                            <span className="ticket-member" title={`${e.Name} ${e.Surname}`} key={e.Id} onClick={() => removeTicketMember(e.Id)}>
                                                {e.Name} {e.Surname[0]}
                                                <i className="fal fa-times"></i>
                                            </span>
                                        );
                                    })}
                                    <select value={0} title="Members" name="ticket-members-select" onChange={addTicketMember}>
                                        <option value={0} disabled>
                                            Add
                                        </option>
                                        {state.clientContacts
                                            .filter((x) => !ticket?.Members.map((y) => y.Id).includes(x.Id))
                                            .map((x: any, i: number) => {
                                                return (
                                                    <option value={x.Id} key={i}>
                                                        {x.Name} {x.Surname}
                                                    </option>
                                                );
                                            })}
                                    </select>
                                </div>
                            )}
                        </div>
                        <div className="input-group">
                            <label>Assignee</label>
                            {!ticket?.Assignee ? (
                                <select value={0} title="Assignee" name="ticket-assignee-select" onChange={assignTicket} onClick={(e) => e.stopPropagation()}>
                                    <option value={0} disabled>
                                        Assign Ticket
                                    </option>
                                    {state.clientContacts.map((x: any, i: number) => {
                                        return (
                                            <option value={x.Id} key={i}>
                                                {x.Name} {x.Surname}
                                            </option>
                                        );
                                    })}
                                </select>
                            ) : (
                                <div id="ticket-assignee" onClick={() => removeAssignee()}>
                                    <i className="fal fa-user-circle"></i>
                                    {ticket?.Assignee.Name} {ticket?.Assignee.Surname}
                                </div>
                            )}
                        </div>
                        <div className="input-group">
                            <label>Due Date</label>
                            <input
                                type="date"
                                value={ticket?.TicketDetails.DueDate ? moment(ticket?.TicketDetails.DueDate).format("YYYY-MM-DD") : ""}
                                onChange={(e) => {
                                    setTicket({
                                        ...ticket!,
                                        TicketDetails: {
                                            ...ticket!.TicketDetails,
                                            DueDate: e.currentTarget.value,
                                        },
                                    });
                                }}
                                onBlur={updateTicket}
                            />
                        </div>
                        <div className="input-group">
                            <label>Labels</label>
                            {ticket?.Labels?.map((x: any, i: number) => (
                                <div className="ticket-label" style={{ background: `#${x.Colour}`, color: `${x.Colour === "FFF" ? "#000" : "#FFF"}` }} onClick={() => removeTicketLabel(x.Id)} key={i}>
                                    {x.Description} <i className="fal fa-times"></i>
                                </div>
                            ))}
                            <div className={`ticket-label new-ticket-label ${newLabel.open ? "open" : ""}`} style={{ background: `${newLabel.open ? `#${newLabel.colour}` : `#FFF`}` }}>
                                <input
                                    type="text"
                                    ref={labelRef}
                                    value={newLabel.description}
                                    onFocus={() => setNewLabel({ ...newLabel, open: true })}
                                    onChange={(e) => setNewLabel({ ...newLabel, description: e.currentTarget.value })}
                                    placeholder={`${newLabel.open ? `Title` : `Add`}`}
                                />
                                <i
                                    className="fal fa-times"
                                    onClick={() =>
                                        setNewLabel({
                                            ...newLabel,
                                            open: false,
                                            colour: "",
                                        })
                                    }
                                ></i>
                                <TwitterPicker onChange={(data) => setNewLabel({ ...newLabel, colour: data.hex.slice(1) })} color={newLabel.colour} />
                                <button onClick={addTicketLabel}>Add</button>
                            </div>
                        </div>
                    </div>
                    <div className="right">
                        <Activity items={ticket?.Activity} />
                    </div>
                </div>
            </div>
            <Loader show={loading} />
        </>
    );
}

export default Ticket;
